import {
  UserIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpOnSquareIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { default as Popover } from 'antd/es/popover';
import cn from 'classnames';
import type { ComponentType, SVGProps } from 'react';

type ActivityKind =
  | 'userCreated'
  | 'apiCreated'
  | 'batchCreated'
  | 'unknownCreated'
  | 'approved'
  | 'cancelled';

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const ACTIVITY_META = {
  userCreated: {
    titlePrefix: 'Created by',
    classes: 'bg-feedback-info-surface text-feedback-info',
    Icon: UserIcon
  },
  apiCreated: {
    titlePrefix: 'Created using',
    classes: 'bg-border-soft text-content-strong',
    Icon: PlusCircleIcon
  },
  batchCreated: {
    titlePrefix: 'Created from',
    classes: 'bg-content-brand-faint text-content-brand',
    Icon: ArrowUpOnSquareIcon
  },
  unknownCreated: {
    titlePrefix: 'Unknown',
    classes: 'bg-border-soft text-content-strong',
    Icon: QuestionMarkCircleIcon
  },
  approved: {
    titlePrefix: 'Approved by',
    classes: 'bg-feedback-success-surface text-feedback-success',
    Icon: CheckIcon
  },
  cancelled: {
    titlePrefix: 'Cancelled by',
    classes: 'bg-feedback-error-surface text-feedback-error',
    Icon: XMarkIcon
  }
} as const satisfies Record<
  ActivityKind,
  { titlePrefix: string; classes: string; Icon: IconLike }
>;

type ActivityBadgeProps = {
  name: string;
  kind: ActivityKind;
  message?: string;
  timestamp?: string;
  className?: string;
};

export const ActivityBadge = ({
  name,
  kind,
  message,
  timestamp,
  className
}: ActivityBadgeProps) => {
  const { Icon, classes, titlePrefix } = ACTIVITY_META[kind];

  const content = (
    <div className="space-y-1">
      {message ? <p className="text-xs">{message}</p> : null}
      {timestamp ? (
        <p className="text-xs font-bold">{`${timestamp} GMT`}</p>
      ) : null}
    </div>
  );

  const title = (
    <div className="font-medium">
      {titlePrefix} {name}
    </div>
  );

  return (
    <Popover content={content} title={title}>
      <div
        className={cn(
          'inline-flex items-center gap-1.5 rounded-2xl px-2 py-0.5 text-sm font-medium',
          classes,
          className
        )}
        aria-label={`${titlePrefix} ${name}`}
      >
        <Icon className="size-4 stroke-2" />
        <span className="truncate">{name}</span>
      </div>
    </Popover>
  );
};
